// ============================================================
// src/pages/MyHistoryPage.jsx
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { getMyHistory, getWeekEntries } from '../api/timesheetApi';
import { Toast, StatCard, StatusBadge, Spinner, CardTitle, PageHeader, Modal } from '../components/UI';
import { useToast } from '../hooks/useToast';

export default function MyHistoryPage() {
  const { toast, showToast, clearToast } = useToast();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', year: '' });
  const [selected,   setSelected]   = useState(null);
  const [entries,    setEntries]    = useState([]);
  const [loadingEnt, setLoadingEnt] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMyHistory(filters);
      setRows(data);
    } catch (err) {
      showToast(err.message || 'Failed to load history', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  async function viewEntries(week) {
    setSelected(week);
    setLoadingEnt(true);
    try {
      const data = await getWeekEntries(week.id);
      setEntries(data);
    } catch {
      setEntries([]);
    } finally {
      setLoadingEnt(false);
    }
  }

  const stats = {
    total:    rows.length,
    approved: rows.filter(r => r.status === 'approved').length,
    pending:  rows.filter(r => r.status === 'submitted').length,
    hrs:      rows.reduce((s, r) => s + parseFloat(r.total_hours || 0), 0).toFixed(1),
  };

  const years = [...new Set(rows.map(r => r.week_ending_date?.slice(6)))].filter(Boolean).sort().reverse();

  return (
    <div>
      <PageHeader title="MY TIMESHEET HISTORY" subtitle="All submitted weeks" />

      {/* <div className="stat-grid mb-4">
        <StatCard label="Total Weeks" value={stats.total}    color="gray" />
        <StatCard label="Approved"    value={stats.approved} color="green" />
        <StatCard label="Pending"     value={stats.pending}  color="yellow" />
        <StatCard label="Total Hours" value={stats.hrs}      sub="all time" color="red" />
      </div> */}

      <div className="filter-row mb-3">
        <select className="sel" style={{ width: 'auto' }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select className="sel" style={{ width: 'auto' }} value={filters.year}
          onChange={e => setFilters(f => ({ ...f, year: e.target.value }))}>
          <option value="">All Years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={fetchHistory}>REFRESH</button>
      </div>

      <div className="card">
        <CardTitle>Timesheet Weeks</CardTitle>
        {loading ? <Spinner /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {['Week Ending','Hrs','Regular','OT','Status','Reviewed By','Notes',''].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0
                  ? <tr><td colSpan={8} className="tbl-empty">No history found.</td></tr>
                  : rows.map(r => (
                    <tr key={r.id}>
                      <td className="td-mono">{r.week_ending_date}</td>
                      <td><strong>{parseFloat(r.total_hours).toFixed(1)}</strong></td>
                      <td className="td-mono td-muted">{parseFloat(r.regular_hours).toFixed(1)}</td>
                      <td>
                        {parseFloat(r.overtime_hours) > 0
                          ? <span className="td-red">{parseFloat(r.overtime_hours).toFixed(1)}</span>
                          : <span className="td-muted">—</span>}
                      </td>
                      <td><StatusBadge status={r.status} /></td>
                      <td className="td-muted">{r.reviewer_name || '—'}</td>
                      <td className="td-trunc" style={{ maxWidth: '10rem' }}>
                        {r.reviewer_notes
                          ? <span className="italic text-muted" style={{ fontSize: '0.75rem' }}>{r.reviewer_notes}</span>
                          : <span className="td-muted">—</span>}
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-xs" onClick={() => viewEntries(r)}>VIEW</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={!!selected}
        title={`Entries – Week Ending ${selected?.week_ending_date || ''}`}
        onClose={() => setSelected(null)} wide>
        {loadingEnt ? <Spinner /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {['Date','WO #','Job #','Description','In','Out','Hrs'].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0
                  ? <tr><td colSpan={7} className="tbl-empty">No entries.</td></tr>
                  : entries.map(en => (
                    <tr key={en.id}>
                      <td className="td-mono">{en.entry_date}</td>
                      <td className="td-mono" style={{ color: '#7c3aed' }}>{en.work_order || '—'}</td>
                      <td className="td-mono td-red">{en.job_number}</td>
                      <td className="td-trunc">{en.description}</td>
                      <td className="td-mono td-green">{en.time_in?.slice(0, 5)}</td>
                      <td className="td-mono td-red">{en.time_out?.slice(0, 5)}</td>
                      <td><strong>{parseFloat(en.hours_worked).toFixed(1)}h</strong></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}
